// Notification WebSocket service using Socket.IO client
import keycloak from '@/auth/keycloak';
import { NOTIFICATION_CONFIG } from '@/config/notifications';
import { NotificationData, detectUserType } from './notification-service';
import { io, Socket } from 'socket.io-client';

// Connection states
export enum NotificationConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting', 
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error',
}

// WebSocket message types
export interface NotificationWebSocketMessage {
  type: string;
  payload: any;
}

// Notification Socket.IO client
export class NotificationSocketClient {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = NOTIFICATION_CONFIG.RECONNECT_ATTEMPTS;
  private reconnectTimer: number | null = null;
  private state: NotificationConnectionState = NotificationConnectionState.DISCONNECTED;
  private messageHandlers: Map<string, (payload: any) => void> = new Map();
  private lastError: Error | null = null;
  private connectionPending = false;
  private manuallyDisconnected = false;
  private connectionTimestamp: number = 0;
  
  // Organization context for Socket.IO connection  
  private currentOrganizationId: string | null = null;
  private currentInstitutionId: string | null = null;
  
  // State change listeners
  private stateChangeListeners: Set<(state: NotificationConnectionState) => void> = new Set();
  
  // Heartbeat/keep-alive mechanism
  private heartbeatTimer: number | null = null;
  private lastPongTimestamp: number = 0;
  private missedPongs = 0;
  private readonly MAX_MISSED_PONGS = 3;
  
  // Visibility change handling
  private boundHandleVisibilityChange: (() => void) | null = null;
  private boundHandleFocus: (() => void) | null = null;
  private boundHandleOnline: (() => void) | null = null;
  
  constructor() {
    console.log('NotificationSocketClient: Initialized');
    this.setupBrowserEventListeners();
  }
  
  // Set up browser event listeners for visibility, focus, and online status
  private setupBrowserEventListeners(): void {
    // Handle tab visibility change
    this.boundHandleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('NotificationSocketClient: Tab became visible, checking connection');
        this.checkAndReconnect();
      }
    };
    
    // Handle window focus
    this.boundHandleFocus = () => {
      console.log('NotificationSocketClient: Window focused, checking connection');
      this.checkAndReconnect();
    };
    
    // Handle coming back online
    this.boundHandleOnline = () => {
      console.log('NotificationSocketClient: Network came online, reconnecting');
      this.checkAndReconnect();
    };
    
    document.addEventListener('visibilitychange', this.boundHandleVisibilityChange);
    window.addEventListener('focus', this.boundHandleFocus);
    window.addEventListener('online', this.boundHandleOnline);
  }
  
  // Check connection health and reconnect if needed
  private checkAndReconnect(): void {
    // Don't reconnect if manually disconnected
    if (this.manuallyDisconnected) {
      console.log('NotificationSocketClient: Skipping reconnect - manually disconnected');
      return;
    }
    
    // Don't reconnect if missing required context
    if (!keycloak.authenticated || !this.currentOrganizationId) {
      console.log('NotificationSocketClient: Skipping reconnect - missing auth or org context');
      return;
    }
    
    // Check if socket is actually connected
    const isActuallyConnected = this.socket?.connected === true;
    
    if (!isActuallyConnected && this.state !== NotificationConnectionState.CONNECTING) {
      console.log('NotificationSocketClient: Connection lost, reconnecting...');
      // Reset reconnect attempts on manual check to give it a fresh start
      this.reconnectAttempts = 0;
      this.connect();
    } else if (isActuallyConnected) {
      // Send a ping to verify connection is truly alive
      this.ping();
    }
  }

  // Set organization context
  setOrganizationContext(organizationId: string | null, institutionId: string | null): void {
    const orgChanged = this.currentOrganizationId !== organizationId;
    const instChanged = this.currentInstitutionId !== institutionId;
    
    console.log('NotificationSocketClient: Setting organization context', {
      organizationId,
      institutionId,
      orgChanged,
      instChanged
    });
    
    this.currentOrganizationId = organizationId;
    this.currentInstitutionId = institutionId;
    
    // Only reconnect if we're already connected and context significantly changed
    // The provider will handle initial connection when context becomes available
    if ((orgChanged || instChanged) && this.state === NotificationConnectionState.CONNECTED) {
      console.log('NotificationSocketClient: Organization context changed, gracefully reconnecting');
      this.disconnect();
      // Don't auto-reconnect here, let the provider handle it
    }
  }

  // Get Socket.IO connection options following complete backend documentation
  private getSocketOptions(): any {
    const options: any = {
      path: NOTIFICATION_CONFIG.WEBSOCKET_PATH,
      query: {
        token: keycloak.token || '',
        organizationId: this.currentOrganizationId || '', // Add as query param for WebSocket compatibility
      },
      extraHeaders: {
        'Authorization': `Bearer ${keycloak.token}`,
        'X-Organization-ID': this.currentOrganizationId || '', // ← CRITICAL: Backend expects "ID" (all caps)
      },
      transports: ['polling', 'websocket'], // Start with polling (supports headers) then upgrade
      withCredentials: true,
      upgrade: true, // Allow upgrade to WebSocket after header validation
      autoConnect: false,
      reconnection: false, // We'll handle reconnection manually
      forceNew: false
    };
    
    return options;
  }

  // Connect to Socket.IO with token and headers
  private async connectWithToken(_token: string): Promise<boolean> {
    const socketOptions = this.getSocketOptions();
    const socketUrl = NOTIFICATION_CONFIG.WEBSOCKET_URL;
    
    console.log(`NotificationSocketClient: Connecting to Socket.IO at: ${socketUrl}`);
    console.log(`NotificationSocketClient: Full URL will be: ${socketUrl}${socketOptions.path}`);
    console.log('NotificationSocketClient: Context will be sent in headers AND query params:', {
      'X-Organization-ID': this.currentOrganizationId, // ← CRITICAL: Backend expects "ID" (all caps)
      'Authorization': `Bearer ${keycloak.token ? '***' : 'MISSING'}`,
      'organizationId (query)': this.currentOrganizationId
    });
    console.log('NotificationSocketClient: Socket options:', socketOptions);
    
    try {
      this.socket = io(socketUrl, socketOptions);
      this.setupSocketListeners();
      
      // Wait for connection to complete or timeout
      const connected = await Promise.race([
        // Success: wait for connect event
        new Promise<boolean>(resolve => {
          if (!this.socket) return resolve(false);
          
          const onConnect = () => {
            this.socket?.off('connect', onConnect);
            resolve(true);
          };
          
          this.socket.on('connect', onConnect);
          this.socket.connect();
        }),
        
        // Timeout
        new Promise<boolean>(resolve => setTimeout(() => resolve(false), NOTIFICATION_CONFIG.CONNECTION_TIMEOUT))
      ]);
      
      if (connected) {
        console.log('NotificationSocketClient: Successfully connected');
        return true;
      }
      
      // Disconnect socket if it's not already disconnected
      if (this.socket && this.socket.connected) {
        this.socket.disconnect();
        this.socket = null;
      }
      
      console.error('NotificationSocketClient: Connection failed to open within timeout');
      return false;
    } catch (error) {
      console.error(`NotificationSocketClient: Failed to connect`, error);
      this.socket = null;
      this.lastError = error instanceof Error ? error : new Error('Connection failed');
      return false;
    }
  }

  // Set up Socket.IO event listeners
  private setupSocketListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('NotificationSocketClient: Socket.IO connection opened');
      this.setState(NotificationConnectionState.CONNECTED);
      this.connectionTimestamp = Date.now();
      console.log('NotificationSocketClient: Connection established at', new Date(this.connectionTimestamp).toISOString());
      this.reconnectAttempts = 0;
      this.lastError = null;
      this.missedPongs = 0;
      
      // Start heartbeat mechanism
      this.startHeartbeat();
      
      // Join notification room following backend documentation
      if (this.currentOrganizationId) {
        const userType = detectUserType();
        const joinPayload = {
          userId: keycloak.tokenParsed?.sub || '',
          userType: userType,
          token: keycloak.token || ''
          // Don't include organizationId here anymore - it's in headers
        };
        
        this.socket?.emit('join', joinPayload);
        console.log('NotificationSocketClient: Joining as', userType, 'user');
      }
    });

    // Listen for successful join confirmation
    this.socket.on('connected', (data: any) => {
      console.log('NotificationSocketClient: Successfully joined notifications:', data);
    });

    // Error handling with specific error codes
    this.socket.on('auth_error', (error: any) => {
      console.error('❌ WebSocket auth error:', error);
      this.setState(NotificationConnectionState.ERROR);
      this.lastError = new Error(`Auth error: ${error.message || error}`);
      
      // Handle specific error codes following backend documentation
      switch (error.code) {
        case 'MISSING_ORGANIZATION_HEADER':
          console.error('Missing organization header');
          break;
        case 'INVALID_TOKEN':
          console.error('Invalid or expired token - attempting token refresh and reconnect');
          // Trigger token refresh and reconnect
          this.handleTokenExpiry();
          break;
        case 'TOKEN_REQUIRED':
          console.error('Token required in production');
          break;
        case 'AUTH_ERROR':
          console.error('General authentication failure');
          // Try token refresh for general auth errors too
          this.handleTokenExpiry();
          break;
        case 'UNAUTHORIZED_USER':
          console.error('User not authorized for action');
          break;
        case 'INSUFFICIENT_PERMISSIONS':
          console.error('User lacks required permissions');
          break;
        default:
          console.error('Unknown auth error:', error);
      }
    });

    this.socket.on('error', (error: any) => {
      console.error('❌ WebSocket error:', error);
      this.lastError = error;
    });

    // Notification events
    this.socket.on('notification', (notification: NotificationData) => {
      console.log('📬 New notification:', notification);
      const handler = this.messageHandlers.get('notification');
      if (handler) {
        handler(notification);
      }
      
      // Show browser notification if permission granted
      this.showBrowserNotification(notification);
    });

    this.socket.on('notifications_history', (data: any) => {
      console.log('📋 Notification history received:', data);
      const handler = this.messageHandlers.get('notifications_history');
      if (handler) {
        handler(data);
      }
    });

    this.socket.on('notification_read', (data: { notificationId: string; readAt: string }) => {
      console.log('NotificationSocketClient: Notification marked as read:', data);
      const handler = this.messageHandlers.get('notification_read');
      if (handler) {
        handler(data);
      }
    });

    // Heartbeat
    this.socket.on('pong', (data: any) => {
      console.log('🏓 Pong received');
      this.lastPongTimestamp = Date.now();
      this.missedPongs = 0; // Reset missed pongs on successful response
      const handler = this.messageHandlers.get('pong');
      if (handler) {
        handler(data);
      }
    });

    this.socket.on('disconnect', (reason: string) => {
      console.log('NotificationSocketClient: Socket.IO connection closed', { reason });
      
      if (!this.manuallyDisconnected) {
        this.setState(NotificationConnectionState.DISCONNECTED);
        this.scheduleReconnect();
      } else {
        this.setState(NotificationConnectionState.DISCONNECTED);
      }
    });

    this.socket.on('connect_error', (error: Error) => {
      console.error('❌ WebSocket connection error:', error);
      this.setState(NotificationConnectionState.ERROR);
      this.lastError = error;
    });
  }

  // Helper method for browser notifications following backend documentation
  private showBrowserNotification(notification: NotificationData): void {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
      });
    }
  }

  // Send message to server using Socket.IO emit
  private emit(event: string, data: any): boolean {
    if (!this.socket || !this.socket.connected) {
      console.warn('NotificationSocketClient: Cannot send message, Socket.IO not connected');
      return false;
    }

    try {
      this.socket.emit(event, data);
      return true;
    } catch (error) {
      console.error('NotificationSocketClient: Failed to emit message:', error);
      return false;
    }
  }

  // Set connection state and notify listeners
  private setState(newState: NotificationConnectionState): void {
    if (this.state !== newState) {
      const oldState = this.state;
      this.state = newState;
      console.log(`NotificationSocketClient: State changed from ${oldState} to ${newState}`);
      
      // Notify all listeners
      this.stateChangeListeners.forEach((listener) => {
        try {
          listener(newState);
        } catch (error) {
          console.error('NotificationSocketClient: Error in state change listener:', error);
        }
      });
    }
  }

  // Schedule reconnection with exponential backoff
  // Now continues indefinitely with longer delays after initial attempts
  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    // Don't reconnect if manually disconnected
    if (this.manuallyDisconnected) {
      console.log('NotificationSocketClient: Skipping scheduled reconnect - manually disconnected');
      return;
    }

    // Calculate delay with exponential backoff
    // After maxReconnectAttempts, switch to a slower retry interval (60 seconds)
    let delay: number;
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      // Fast reconnect attempts with exponential backoff (3s, 6s, 12s, 24s, 30s)
      delay = Math.min(
      NOTIFICATION_CONFIG.RECONNECT_DELAY * Math.pow(2, this.reconnectAttempts),
        30000 // Max 30 seconds for fast phase
    );
    } else {
      // Slow reconnect phase - try every 60 seconds indefinitely
      delay = 60000;
      console.log('NotificationSocketClient: Entered slow reconnect phase (every 60s)');
    }

    console.log(`NotificationSocketClient: Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);
    
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectAttempts++;
      this.setState(NotificationConnectionState.RECONNECTING);
      this.connect();
    }, delay);
  }
  
  // Reset reconnect attempts - called when user manually triggers reconnect
  resetReconnectAttempts(): void {
    console.log('NotificationSocketClient: Resetting reconnect attempts');
    this.reconnectAttempts = 0;
    this.manuallyDisconnected = false;
  }

  // Public API methods

  // Connect to notification WebSocket
  async connect(): Promise<void> {
    if (this.connectionPending || this.state === NotificationConnectionState.CONNECTED || this.state === NotificationConnectionState.CONNECTING) {
      console.log('NotificationSocketClient: Connection already in progress or established');
      return;
    }

    if (!keycloak.authenticated || !keycloak.token) {
      console.warn('NotificationSocketClient: Cannot connect without authentication');
      return;
    }

    if (!this.currentOrganizationId) {
      console.warn('NotificationSocketClient: Cannot connect without organization context');
      return;
    }

    this.connectionPending = true;
    this.manuallyDisconnected = false;
    this.setState(NotificationConnectionState.CONNECTING);

    try {
      // Ensure token is fresh
      if (keycloak.isTokenExpired(30)) {
        console.log('NotificationSocketClient: Refreshing token before connection');
        await keycloak.updateToken(60);
      }

      const connected = await this.connectWithToken(keycloak.token!);
      
      if (!connected) {
        throw new Error('Failed to establish connection');
      }
    } catch (error) {
      console.error('NotificationSocketClient: Connection failed:', error);
      this.setState(NotificationConnectionState.ERROR);
      this.lastError = error instanceof Error ? error : new Error('Connection failed');
      this.scheduleReconnect();
    } finally {
      this.connectionPending = false;
    }
  }

  // Disconnect from Socket.IO
  disconnect(): void {
    console.log('NotificationSocketClient: Manually disconnecting');
    
    this.manuallyDisconnected = true;
    
    // Stop heartbeat
    this.stopHeartbeat();
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.setState(NotificationConnectionState.DISCONNECTED);
  }
  
  // Start heartbeat mechanism to keep connection alive
  private startHeartbeat(): void {
    this.stopHeartbeat(); // Clear any existing heartbeat
    
    console.log('NotificationSocketClient: Starting heartbeat (interval:', NOTIFICATION_CONFIG.HEARTBEAT_INTERVAL, 'ms)');
    this.lastPongTimestamp = Date.now();
    this.missedPongs = 0;
    
    this.heartbeatTimer = window.setInterval(() => {
      if (this.state === NotificationConnectionState.CONNECTED && this.socket?.connected) {
        // Check if we've missed too many pongs (connection might be zombie)
        const timeSinceLastPong = Date.now() - this.lastPongTimestamp;
        
        if (timeSinceLastPong > NOTIFICATION_CONFIG.HEARTBEAT_INTERVAL * 2) {
          this.missedPongs++;
          console.warn(`NotificationSocketClient: Missed pong response (${this.missedPongs}/${this.MAX_MISSED_PONGS})`);
          
          if (this.missedPongs >= this.MAX_MISSED_PONGS) {
            console.error('NotificationSocketClient: Too many missed pongs, connection appears dead. Reconnecting...');
            this.stopHeartbeat();
            this.socket?.disconnect();
            this.scheduleReconnect();
            return;
          }
        }
        
        // Send heartbeat ping
        this.ping();
      }
    }, NOTIFICATION_CONFIG.HEARTBEAT_INTERVAL);
  }
  
  // Stop heartbeat mechanism
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
  
  // Handle token expiry - refresh token and reconnect
  private async handleTokenExpiry(): Promise<void> {
    console.log('NotificationSocketClient: Handling token expiry...');
    
    // Disconnect current socket
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.stopHeartbeat();
    
    // Clear any existing reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    try {
      // Try to refresh the token
      if (keycloak.authenticated) {
        console.log('NotificationSocketClient: Refreshing Keycloak token...');
        await keycloak.updateToken(60);
        console.log('NotificationSocketClient: Token refreshed successfully');
        
        // Reset state and reconnect with fresh token
        this.reconnectAttempts = 0;
        this.manuallyDisconnected = false;
        this.setState(NotificationConnectionState.DISCONNECTED);
        
        // Schedule a reconnect - store timer ID so it can be cancelled if needed
        this.reconnectTimer = window.setTimeout(() => {
          this.reconnectTimer = null;
          this.connect();
        }, 1000);
      }
    } catch (error) {
      console.error('NotificationSocketClient: Token refresh failed:', error);
      this.setState(NotificationConnectionState.ERROR);
      this.lastError = new Error('Token refresh failed');
    }
  }

  // Mark notification as read
  markAsRead(notificationId: string): boolean {
    return this.emit('mark_read', { notificationId });
  }

  // Request notification history following backend documentation
  requestHistory(limit = 20, offset = 0): boolean {
    if (keycloak.tokenParsed?.sub) {
      const payload = {
        userId: keycloak.tokenParsed.sub,
        limit,
        offset,
      };
      return this.emit('get_notifications', payload);
    }
    return false;
  }

  // Legacy method for backwards compatibility
  getNotifications(limit = 20, offset = 0): boolean {
    return this.requestHistory(limit, offset);
  }

  // Send heartbeat ping
  ping(): boolean {
    return this.emit('ping', {});
  }

  // Register message handlers following backend documentation
  onNotification(handler: (notification: NotificationData) => void): void {
    this.messageHandlers.set('notification', handler);
  }

  onNotificationsHistory(handler: (data: any) => void): void {
    this.messageHandlers.set('notifications_history', handler);
  }

  onNotificationRead(handler: (data: { notificationId: string; readAt: string }) => void): void {
    this.messageHandlers.set('notification_read', handler);
  }

  onPong(handler: (data: any) => void): void {
    this.messageHandlers.set('pong', handler);
  }

  // State change listener management
  onStateChange(listener: (state: NotificationConnectionState) => void): void {
    this.stateChangeListeners.add(listener);
  }

  removeStateChangeListener(listener: (state: NotificationConnectionState) => void): void {
    this.stateChangeListeners.delete(listener);
  }

  // Getters
  getState(): NotificationConnectionState {
    return this.state;
  }

  isConnected(): boolean {
    return this.state === NotificationConnectionState.CONNECTED && this.socket?.connected === true;
  }

  getLastError(): Error | null {
    return this.lastError;
  }
  
  // Get connection info for debugging
  getConnectionInfo(): {
    state: NotificationConnectionState;
    isSocketConnected: boolean;
    reconnectAttempts: number;
    lastPongTime: number;
    missedPongs: number;
    connectionAge: number;
  } {
    return {
      state: this.state,
      isSocketConnected: this.socket?.connected === true,
      reconnectAttempts: this.reconnectAttempts,
      lastPongTime: this.lastPongTimestamp,
      missedPongs: this.missedPongs,
      connectionAge: this.connectionTimestamp ? Date.now() - this.connectionTimestamp : 0,
    };
  }
  
  // Manual reconnect - resets attempts and forces fresh connection
  async forceReconnect(): Promise<void> {
    console.log('NotificationSocketClient: Force reconnecting...');
    
    // Stop everything
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    
    // Reset state
    this.reconnectAttempts = 0;
    this.manuallyDisconnected = false;
    this.connectionPending = false;
    this.setState(NotificationConnectionState.DISCONNECTED);
    
    // Refresh token and connect
    if (keycloak.authenticated) {
      try {
        await keycloak.updateToken(30);
      } catch (e) {
        console.warn('NotificationSocketClient: Token refresh failed during force reconnect:', e);
      }
    }
    
    // Connect
    await this.connect();
  }
  
  // Cleanup - remove browser event listeners
  cleanup(): void {
    console.log('NotificationSocketClient: Cleaning up...');
    
    this.stopHeartbeat();
    
    if (this.boundHandleVisibilityChange) {
      document.removeEventListener('visibilitychange', this.boundHandleVisibilityChange);
    }
    if (this.boundHandleFocus) {
      window.removeEventListener('focus', this.boundHandleFocus);
    }
    if (this.boundHandleOnline) {
      window.removeEventListener('online', this.boundHandleOnline);
    }
    
    this.disconnect();
  }
}

// Singleton instance
const notificationSocketService = new NotificationSocketClient();
export default notificationSocketService;
